import { useMutation } from "@tanstack/react-query";

import { AttributeCard } from "@/components/Card/AttributeCard";
import Icon from "@/components/Icon/Icon";
import { Grid, HStack, VStack } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import type { ApiResponseBody } from "@/lib/api/api-type-helper";
import { apiClient } from "@/lib/api/client";
import { downloadFile } from "@/lib/api/utils";
import { defaultNS, useTranslation } from "@/lib/i18n";

import type { ExportApiUrl } from "../../types";

type Props = {
  exportApiUrl: ExportApiUrl;
  exportFilename: string;
  templateMessage: {
    templateTitle: string;
    updateExisting: string;
  };
};

const DownloadTemplateCard = ({
  exportApiUrl,
  exportFilename,
  templateMessage,
}: Props) => {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "pages.person.importCsv.prepareCsvFile",
  });

  const { mutate } = useMutation({
    mutationKey: [exportApiUrl, exportFilename],
    mutationFn: (mode: "edit" | "edit-template") =>
      apiClient.GET(exportApiUrl, {
        params: { query: { mode, encoding: "UTF-8" } },
      }),
    onSuccess: async (data) => {
      const csvData = data.data as ApiResponseBody<
        "/v2/app-integrations/{id}/users-csv",
        "get"
      >;
      if (csvData?.url && csvData?.mode) {
        const filename =
          csvData.mode === "edit"
            ? `${exportFilename}.csv`
            : `${t("template")}_${exportFilename}.csv`;
        await downloadFile(csvData.url, filename);
      }
    },
  });

  return (
    <AttributeCard
      title={t("title")}
      titleStyle="CardHeader"
      className="w-full"
    >
      <Grid columns={2} columnGap={24}>
        <VStack className="gap-3">
          <Label className="text-muted-foreground">
            {templateMessage.templateTitle}
          </Label>
          <Button
            variant="outline"
            className="justify-between items-center h-12 bg-neutral-bottom-fill"
            onClick={() => mutate("edit-template")}
          >
            <HStack className="gap-1 items-center">
              <Icon iconName="FileSpreadsheet" />
              <span>{`${t("template")}_${exportFilename}.csv`}</span>
            </HStack>
            <HStack className="gap-1 items-center">
              <Icon iconName="Download" />
              <span>{t("download")}</span>
            </HStack>
          </Button>
        </VStack>
        {/* TODO: Use last uploaded file機能追加時に実装 */}
        {/* <VStack className="gap-3">
          <Label className="text-muted-foreground">{t("useLastUploadedFile")}</Label>
          <Button
            variant="outline"
            className="justify-between items-center h-12 bg-neutral-bottom-fill"
          >
            <HStack className="gap-1 items-center">
              <Icon iconName="FileSpreadsheet" />
              <span>Uploaded file name.csv</span>
            </HStack>
            <HStack className="gap-1 items-center">
              <Icon iconName="Download" />
              <span>{t("download")}</span>
            </HStack>
          </Button>
        </VStack> */}
        <VStack className="gap-3">
          <Label className="text-muted-foreground">
            {templateMessage.updateExisting}
          </Label>
          <Button
            variant="outline"
            className="justify-between items-center h-12 bg-neutral-bottom-fill"
            onClick={() => mutate("edit")}
          >
            <HStack className="gap-1 items-center">
              <Icon iconName="FileSpreadsheet" />
              <span>{exportFilename}.csv</span>
            </HStack>
            <HStack className="gap-1 items-center">
              <Icon iconName="Download" />
              <span>{t("download")}</span>
            </HStack>
          </Button>
        </VStack>
      </Grid>
    </AttributeCard>
  );
};

export default DownloadTemplateCard;
